// @aiContributed-MAPFuse-2026-06-04
import type { I18nLike } from '@bingads-webui/common-types';
import './global.types';

export const bidStratetyTypeValue = {
  parent: 'InheritFromParent',
  manual: 'ManualCpc',
  manualCpv: 'ManualCpv',
  manualCpm: 'ManualCpm',
  manualCpa: 'ManualCpa',
  enhancedCPC: 'EnhancedCpc',
  maximizeClicks: 'MaxClicks',
  maximizeConversions: 'MaxConversions',
  targetCpa: 'TargetCpa',
  targetRoas: 'TargetRoas',
  maxConversionValue: 'MaxConversionValue',
  targetImpressionShare: 'TargetImpressionShare',
  portfolioBidStrategy: 'PortfolioBidStrategy',
  costPerSale: 'CostPerSale',
  percentCpc: 'PercentCpc',
  commission: 'Commission',
};

export const biddingStrategyTypeId = {
  InheritFromParent: 0,
  ManualCpc: 1,
  MaxClicks: 2,
  MaxConversions: 3,
  TargetCpa: 4,
  EnhancedCpc: 5,
  TargetRoas: 6,
  MaxConversionValue: 8,
  TargetImpressionShare: 9,
  ManualCpv: 11,
  ManualCpm: 12,
  PercentCpc: 13,
  Commission: 14,
  CostPerSale: 15,
  ManualCpa: 16,
};

export const biddingStrategyIdToType = {
  0: 'InheritFromParent',
  1: 'ManualCpc',
  2: 'MaxClicks',
  3: 'MaxConversions',
  4: 'TargetCpa',
  5: 'EnhancedCpc',
  6: 'TargetRoas',
  8: 'MaxConversionValue',
  9: 'TargetImpressionShare',
  11: 'ManualCpv',
  12: 'ManualCpm',
  13: 'PercentCpc',
  14: 'Commission',
  15: 'CostPerSale',
  16: 'ManualCpa',
};

// other campaign types may have different texts for some options
export const searchCampaignBiddingStrategyTypeOptionText = {
  ManualCpc: _TL_('Manual CPC: Use the bid I set'),
  MaxClicks: _TL_('Maximize clicks (automated bidding): Set my bids to get the most clicks'),
  MaxConversions: _TL_('Maximize conversions (automated bidding): Set my bids to get the most conversions'),
  TargetCpa: _TL_('Target CPA (automated bidding): Set target CPA (cost per acquisition)'),
  EnhancedCpc: _TL_('Enhanced CPC: Adjust my bid to get more conversions'),
  TargetRoas: _TL_('Target ROAS (automated bidding): Set target ROAS (return on ad spend)'),
  MaxConversionValue: _TL_('Maximize conversion value (automated bidding): Set my bids to get the most conversion value'),
  TargetImpressionShare: _TL_('Target impression share (automated bidding)'),
  ManualCpv: _TL_('Manual CPV: Use the bid I set'),
  ManualCpm: _TL_('Manual CPM: Use the bid I set'),
  PercentCpc: _TL_('Percentage from room price (per click): a percentage of the room price per night when a guest clicks on your ad'),
  Commission: _TL_('Percentage of room price (per stay): a percentage of the room booking value for stays only, excluding cancellations '),
  CostPerSale: _TL_('Cost Per Sale (automated bidding): Set my bids to get the target cost per sale'),
  ManualCpa: _TL_('Manual CPA: Use the bid I set for each install'),
  PortfolioBidStrategy: _TL_('Portfolio bid strategy: Manage bidding across multiple campaigns'),
};

export const adgroupBiddingStrategyTypeOptionText = {
  InheritFromParent: _TL_("Use the campaign's bid strategy"),
  ManualCpc: _TL_('Use the bid I set'),
};

export const keywordBiddingStrategyTypeOptionText = {
  InheritFromParent: _TL_("Use the ad group's bid strategy"),
  ManualCpc: _TL_('Use the bid I set'),
};

export const biddingStrategyPortfolioOptionText = {
  ManualCpc: _TL_('Manual CPC'),
  MaxClicks: _TL_('Auto: Max Clicks'),
  MaxConversions: _TL_('Auto: Max Conversions'),
  TargetCpa: _TL_('Auto: Target CPA'),
  EnhancedCpc: _TL_('Enhanced CPC'),
  TargetRoas: _TL_('Auto: Target ROAS'),
  MaxConversionValue: _TL_('Auto: Maximize conversion value'),
  TargetImpressionShare: _TL_('Auto: Target impression share'),
  PercentCpc: _TL_('Percent CPC'),
  Commission: _TL_('Commission'),
  ManualCpa: _TL_('Manual CPA'),
};

export type SearchCampaignBiddingStrategyType = keyof typeof searchCampaignBiddingStrategyTypeOptionText;

export type BidStrategyI18n = Pick<I18nLike, 'getString'>;

export const getBidStrategyTitleDetails = (i18n: BidStrategyI18n, bidType?: string) => {
  const optionText = searchCampaignBiddingStrategyTypeOptionText as Record<string, string>;

  if (!bidType || !optionText[bidType]) {
    return '';
  }

  return i18n.getString(optionText[bidType]);
};

const portfoliolevelSupportedBidStrategyReportTypes = [
  'MaxClicks',
  'TargetCpa',
  'TargetRoas',
  'MaxConversions',
  'MaxConversionValue',
  'TargetImpressionShare',
];

const supportedBidStrategyReportTypes = [
  'EnhancedCpc',
  'MaxClicks',
  'TargetCpa',
  'TargetRoas',
  'MaxConversions',
  'MaxConversionValue',
  'TargetImpressionShare',
];

/**
 * Gets supported bid strategy types for portfolio or campaign level
 * @param {boolean} isPortfolio - Whether this is a portfolio level bid strategy
 * @returns {Array<string>} Array of supported bid strategy types
 */
export const getSupportedBidStrategyReportTypes = (isPortfolio: boolean) => {
  if (isPortfolio) {
    return portfoliolevelSupportedBidStrategyReportTypes;
  }
  return supportedBidStrategyReportTypes;
};
